import { trigger } from '@angular/animations';
import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  createCollapseAnimationPromises,
  createExpandAnimationPromises,
  pgExpandableCardTransitionBackdrop,
} from './pg-expandable-card.animations';
import { AnimationService } from '../../core/ui/animation.service';

@Component({
  selector: 'pg-expandable-card, grit-expandable-card',
  exportAs: 'gritExpandableCard',
  templateUrl: './pg-expandable-card.component.html',
  styleUrls: ['./pg-expandable-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('transitionBackdrop', pgExpandableCardTransitionBackdrop),
  ],
})
export class PgExpandableCardComponent
  implements AfterViewInit, OnDestroy, OnChanges {
  expanded = false;
  stickyHeaderTop = 0;
  showContentShadow = false;
  expandAnimationDuration = 250;
  transitioning = false;

  resize$ = new Subject<void>();

  @HostListener('window:resize')
  onResize(): void {
    this.resize$.next();
  }

  get expandState() {
    return this.expanded ? 'expanded' : 'collapsed';
  }

  @Output()
  expandChange = new EventEmitter<boolean>();

  @Output()
  backdropClick = new EventEmitter<any>();

  @Output()
  cardClick = new EventEmitter<any>();

  // The origin element is the target element's bounding rectangle that the
  // card expands to when clicked. By default it is document.body but
  // can be specified to have cards animate to different places on the screen.
  @Input()
  originElement: HTMLElement;

  @Input()
  allowBodyToScroll = false;

  @Input()
  bottomOffset = 0;

  @Input()
  topOffset = 0;

  // the expandPadding input defines how much
  // padding we give to the origin element
  // when the card is fully expanded.
  // it defaults to 16px
  @Input()
  expandPadding = 16;

  @ViewChild('expandContent', { static: true })
  _expandContentChild: ElementRef;
  get expandContentEl(): HTMLElement {
    return this._expandContentChild.nativeElement;
  }

  @ViewChild('content', { static: true })
  _contentChild: ElementRef;
  get contentEl(): HTMLElement {
    return this._contentChild.nativeElement;
  }

  @ViewChild('cardPlaceholder', { static: true })
  _cardPlaceholderChild: ElementRef;
  get cardPlaceholderEl(): HTMLDivElement {
    return this._cardPlaceholderChild.nativeElement;
  }

  @ViewChild('card', { read: ElementRef, static: true })
  _cardChild: ElementRef;
  get cardEl(): HTMLElement {
    return this._cardChild.nativeElement;
  }

  @ViewChild('scrollWrapper', { static: true })
  _scrollWrapperChild;
  get scrollWrapperEl(): HTMLElement {
    return this._scrollWrapperChild.nativeElement;
  }

  @ViewChild('cardParent', { static: true })
  _cardParentChild: ElementRef;
  get cardParentEl(): HTMLElement {
    return this._cardParentChild.nativeElement;
  }

  elevation = 1;

  get originBounds() {
    let originBox;
    if (this.originElement) {
      originBox = this.originElement.getBoundingClientRect();
    } else {
      originBox = {
        width: window.innerWidth,
        height: window.innerHeight,
        top: 0,
        left: 0,
      };
    }
    return originBox;
  }
  get hostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  constructor(
    private _elementRef: ElementRef,
    private _renderer: Renderer2,
    private _animation: AnimationService,
    private _platform: Platform
  ) {}

  ngAfterViewInit() {
    this.resize$.pipe(debounceTime(150)).subscribe(() => {
      this._setExpandedCardSize();
      this._setHiddenExpandContentStyles();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.originElement) {
      this._setHiddenExpandContentStyles();
    }
  }

  ngOnDestroy() {
    // Ensures we bring any expanded content back to the host element
    // for cleanup before the component is destroyed.
    this._moveContentToHostEl();
  }

  handleCardClick(event: Event) {
    event.stopPropagation();
    this.cardClick.emit();
  }

  handleCardScroll(event) {
    if (event.target.scrollTop > -this.stickyHeaderTop) {
      this.showContentShadow = true;
    } else {
      this.showContentShadow = false;
    }
  }

  // The expand function animates the card element
  // from a relative positioned "preview" display
  // to a fixed-position "expanded" display.
  // There is also a placeholder element which holds the
  // place of the fixed-position card while it is expanded
  // so not to change the document flow layout.
  expand() {
    if (this.expanded || this.transitioning) {
      return;
    }
    this.transitioning = true;
    this.expanded = true;
    const originBox = this.originBounds;

    // Consuming apps can specify if they want
    // the body to be scrollable while the card is
    // expanded. This function doesn't prevent
    // scrolling in iOS
    this._setCanBodyScroll(this.allowBodyToScroll);

    // In preparation for the animation we set the expandContentEl
    // to display: block, so that we can calculate the height of the
    // content to add to the target height to animate our card to.
    //
    // In doing so, we have to put in a hacky browser fix as the
    // client height will not be immediately available for calculation
    // so we wrap the rest of the animation in a setTimeout callback to
    // give the browser time to calculate the height of the expand content.
    this._renderer.setStyle(this.expandContentEl, 'display', 'block');

    return new Promise(res => {
      setTimeout(() => {
        // In order to animate the card to the proper location
        // and maintain a relative positioned placeholder in
        // it's place we start by calculating target locations
        // and caching the client rects of all the elements involved in the animations.
        const cardBoundingBox = this.cardEl.getBoundingClientRect();
        const expandContentBoundingBox = this.expandContentEl.getBoundingClientRect();
        const originBoundingBox = originBox;
        const parentWidth = originBoundingBox.width;

        // The target* properties indicate where the card is going to animate to
        // The input expandPadding is taken into account with these calculations
        // in order to provide some user-configurable space to the expanded card.
        const targetWidth = parentWidth - this.expandPadding * 2;
        const targetTop = originBoundingBox.top + this.expandPadding;
        const targetLeft = originBoundingBox.left + this.expandPadding;
        const targetHeight =
          cardBoundingBox.height + expandContentBoundingBox.height;
        this.stickyHeaderTop = -targetTop;

        // The card element itself is wrapped with a scrollWrapperEl that receives
        // the fixed position styles. We make the actual visual card element
        // an absolutely positioned child of the scrollWrapper in order to
        // allow the card to be scrolled incase it is taller than the viewport.
        this._setElementToFixedPositionForExpandView(this.scrollWrapperEl);
        this._setElementToAbsolutePositonForExpandView(
          this.cardEl,
          cardBoundingBox
        );

        // We prep the expanded content element for animation
        // by setting it to visible and preventing pointer events
        // for the duration of the animation
        this._setPreAnimationStylesForShowingExpandedContent(
          this.expandContentEl
        );

        // Now we set the placeholder element to the height of the card
        // in it's preview state so we don't change the layout of the rest of
        // the document.
        this._setElementHeight(this.cardPlaceholderEl, cardBoundingBox.height);

        // Now we want to swap which parent element the card
        // is rendered to so that the card and backdrop are direct children
        // of the body element to prevent parent element styles from
        // interfering with the fixed positioning and transforming.
        //
        // NOTE: this was a fix to accomodate Ionic, it would be worth
        // trying to understand what specifically in Ionic is messing with user styles.
        this._moveContentToBody();

        const promises = createExpandAnimationPromises(
          this._animation,
          this.cardEl,
          this.expandContentEl,
          cardBoundingBox,
          {
            width: targetWidth,
            height: targetHeight,
            left: targetLeft,
            top: targetTop,
          },
          this.expandAnimationDuration
        );

        // The elevation property maps to a box-shadow style
        // in the template, and in expanded mode we want to
        // show the box shadow at elevation 3.
        this.elevation = 3;

        Promise.all(promises)
          .then(el => {
            // When the animations finish we destroy the
            // animations resolved in the promise. This
            // is because, in the webAnimationsApi, animation styles
            // override all css styles
            // until the animations are destroyed
            el.forEach(d => d[0].animation.destroy());

            // We return the pointer events to the expanded content
            // that we disabled earlier in the timeline.
            this._renderer.setStyle(
              this.expandContentEl,
              'pointer-events',
              'auto'
            );
            // Safari doesn't like using the renderer to set pointer events
            // And if we use this._renderer to set pointer events it won't work.
            this.expandContentEl.style.pointerEvents = 'auto';

            // We have to set these styles after the animation because
            // by destroying the animation we will end up resetting the styles
            this._renderer.setStyle(this.expandContentEl, 'opacity', 1);

            //    Height is set to null because we want card height changes
            //    while the card is expanded to take place automatically
            this._renderer.setStyle(this.cardEl, 'height', null);
            this._renderer.setStyle(this.cardEl, 'width', `${targetWidth}px`);
            this._renderer.setStyle(
              this.cardEl,
              'transform',
              `translateX(${targetLeft}px) translateY(${targetTop}px)`
            );
            this.transitioning = false;
            this.expandChange.emit(true);
          })
          .then(() => res());
      }, 0);
    });
  }

  // The collapse function animates the card from it's
  // fixed-position "expanded" state back to it's position
  // in the DOM as a relative positioned element, while not
  // interrupting the normal layout of the document.
  collapse() {
    if (!this.expanded || this.transitioning) {
      return;
    }
    this.transitioning = true;
    this.expanded = false;

    // Let the body element scroll.
    this._setCanBodyScroll(true);

    // Using the same principals as the expand method,
    // we are going to want to calculate the bounding boxes and
    // animation targets upfront. But since everything is already visible
    // There is no need to put this in a set timeout function
    const cardBoundingBox = this.cardEl.getBoundingClientRect();
    this.scrollWrapperEl.scrollTop = 0;
    const placeholderBoundingBox = this.cardPlaceholderEl.getBoundingClientRect();
    const targetWidth = placeholderBoundingBox.width;
    const targetHeight = placeholderBoundingBox.height;
    const targetTop = placeholderBoundingBox.top;
    const targetLeft = placeholderBoundingBox.left;

    const promises = createCollapseAnimationPromises(
      this._animation,
      this.cardEl,
      this.expandContentEl,
      cardBoundingBox,
      {
        width: targetWidth,
        height: targetHeight,
        left: targetLeft,
        top: targetTop,
      },
      this.expandAnimationDuration
    );

    // At the end of the animation the card should be in "preview" mode
    // with the expand content hidden
    // and it will be in a fixed position hovering over the placeholder element
    // so now we need to change a few styles to put it back into the document flow
    // while trying not to interrupt the rest of the document.
    return Promise.all(promises).then(el => {
      this._setElementToRelativePositionForPreview(this.scrollWrapperEl);
      this.elevation = 1;

      this._renderer.setStyle(this.cardPlaceholderEl, 'height', null);
      this._setPostAnimationStylesForHidingExpandedContent(
        this.expandContentEl
      );

      this._setElementToRelativePositionForPreview(this.cardEl);
      // FIX FOR IE
      if (this._platform.TRIDENT || this._platform.EDGE) {
        setTimeout(() => {
          const _el: HTMLElement = document.querySelector('.expandable-card');
          _el.style.transform = '';
          this.cardEl.style.transform = '';
        }, 250);
      }
      this.showContentShadow = false;

      // This returns the card content and backdrop to being
      // children of the host element so they fit in the document
      // layout appropriately and are cleaned up on destroy
      //
      // NOTE: this was a fix to accomodate Ionic,
      // By default Ionic applies the CSS contain: strict property
      // to their content wrappers which changes the bounds by
      // which fixed positioning works.
      this._moveContentToHostEl();

      // When the animations finish we destroy the
      // animations resolved in the promise. This
      // is because, in the webAnimationsApi, animation styles
      // override all css styles
      // until the animations are destroyed
      el.forEach(d => d[0].animation.destroy());
      this.transitioning = false;

      this._setHiddenExpandContentStyles();

      this.expandChange.emit(false);
    });
  }

  // The element passed to this functions should be
  // the element intended to have the fixed position styles
  // applied when the card is expanded.
  private _setElementToFixedPositionForExpandView(el: HTMLElement) {
    this._renderer.setStyle(el, 'top', `${this.topOffset}px`);
    this._renderer.setStyle(el, 'left', `0px`);
    this._renderer.setStyle(el, 'bottom', `${this.bottomOffset}px`);
    this._renderer.setStyle(el, 'right', `0px`);
    this._renderer.setStyle(el, 'position', 'fixed');
    this._renderer.setStyle(el, 'z-index', 160000);
  }

  private _setElementToAbsolutePositonForExpandView(
    el: HTMLElement,
    boundingBox: ClientRect
  ) {
    if (!boundingBox) {
      boundingBox = el.getBoundingClientRect();
    }
    this._renderer.setStyle(el, 'top', `${boundingBox.top}px`);
    this._renderer.setStyle(el, 'left', `${boundingBox.left}px`);
    this._renderer.setStyle(el, 'top', `0px`);
    this._renderer.setStyle(el, 'left', `0px`);
    this._renderer.setStyle(el, 'transform', `translate(${0}px,${0}px)`);
    this._renderer.setStyle(el, 'width', `${boundingBox.width}px`);
    this._renderer.setStyle(el, 'height', `${boundingBox.height}px`);
    this._renderer.setStyle(el, 'position', 'absolute');
  }

  private _setElementToRelativePositionForPreview(el: HTMLElement) {
    this._renderer.setStyle(el, 'top', null);
    this._renderer.setStyle(el, 'left', null);
    this._renderer.setStyle(el, 'bottom', null);
    this._renderer.setStyle(el, 'right', null);
    this._renderer.setStyle(el, 'width', null);
    this._renderer.setStyle(el, 'height', null);
    this._renderer.setStyle(el, 'transform', null);
    this._renderer.setStyle(el, 'position', 'relative');
    this._renderer.setStyle(el, 'z-index', 0);
  }

  private _setPreAnimationStylesForShowingExpandedContent(el: HTMLElement) {
    this._renderer.setStyle(el, 'position', 'relative');
    this._renderer.setStyle(el, 'visibility', 'visible');
    this._renderer.setStyle(el, 'pointer-events', 'none');
  }
  private _setPostAnimationStylesForHidingExpandedContent(el: HTMLElement) {
    this._renderer.setStyle(el, 'opacity', 0);
    // this._renderer.setStyle(el, 'display', 'none');
    this._renderer.setStyle(el, 'visibility', 'hidden');
    this._renderer.setStyle(el, 'position', 'absolute');
    this._renderer.setStyle(el, 'pointer-events', 'none');
  }

  // NOTE: this method doesn't work on ios Safari
  private _setCanBodyScroll(canScroll) {
    if (canScroll) {
      this._renderer.setStyle(document.body, 'overflow', null);
      if (this._platform.TRIDENT || this._platform.EDGE) {
        document.body.style.overflow = 'auto';
      }
    } else {
      this._renderer.setStyle(document.body, 'overflow', 'hidden');
    }
  }

  private _setElementHeight(el, height) {
    if (height === null) {
      this._renderer.setStyle(el, 'height', null);
    } else {
      this._renderer.setStyle(el, 'height', `${height}px`);
    }
  }

  private _setHiddenExpandContentStyles() {
    this._expandContentChild.nativeElement.style.width = `${this.originBounds
      .width -
      this.expandPadding * 2}px`;
  }

  private _setExpandedCardSize() {
    this._renderer.setStyle(
      this.cardEl,
      'width',
      `${this._cardParentChild.nativeElement.getBoundingClientRect().width -
        this.expandPadding * 2}px`
    );
  }

  // This is a dirty element parent switch to get the fixed position
  // version of the card to the body and back to the host element
  // to prevent ionic styles from messing up the fixed positioning logic
  // Portals do not work in this case because using portals to switch
  // rendering locations causes any manually adjusted styles to be reset,
  // and trying to reapply all of the styles in a quick and performant
  // fashion right before the animation takes place is going to take a
  // more complicated fix overall and restructuring of the component.
  // This is something that will be revisted when there is more time to better
  // architect the component to work with Ionic and Portals.
  private _moveContentToHostEl() {
    this.cardParentEl.parentElement.removeChild(this.cardParentEl);
    this.hostElement.appendChild(this.cardParentEl);
  }
  private _moveContentToBody() {
    this.hostElement.removeChild(this.cardParentEl);
    document.body.appendChild(this.cardParentEl);
  }
}
